import { useState, useEffect } from 'react'
import { Info, Landmark } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { leadsAPI } from '../api'
import type { OrgHeftelserResponse, HeftelseBilStatus } from '../api'
import { localizeApiDetail } from '../../../i18n/apiError'
import { dateLocale } from '../../../i18n/format'
import { formatNorwegianDate } from '../../../utils/dateUtils'

interface Props {
  orgNr: string
}

const STATUS_STYLES: Record<HeftelseBilStatus, string> = {
  heftelse: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300',
  leasing_svv: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
  ingen: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  ikke_sjekket: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
}

function formatBelop(value: number | null): string {
  if (value === null) return '—'
  return `${value.toLocaleString(dateLocale(), { maximumFractionDigits: 0 })} kr`
}

/** Liens on the org's active cars, looked up live in Brreg (#943). An org can
 *  be checked once per 30 days and only with up to 500 active cars; the
 *  backend decides both and sends kan_sjekkes. Each car also costs one unit of
 *  the user's daily Brreg quota, which must cover the whole fleet. */
export default function FinansieringTab({ orgNr }: Props) {
  const { t } = useTranslation('leads')
  const [data, setData] = useState<OrgHeftelserResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    setError(null)
    leadsAPI.getOrgHeftelser(orgNr)
      .then(res => setData(res.data))
      .catch(err => setError(localizeApiDetail(err.response?.data?.detail) || t('finansiering.loadFailed')))
      .finally(() => setLoading(false))
  }, [orgNr, t])

  const hent = () => {
    setChecking(true)
    setError(null)
    leadsAPI.checkOrgHeftelser(orgNr)
      .then(res => setData(res.data))
      .catch(err => {
        // A 429 carries an object; its melding is the text to show
        const d = err.response?.data?.detail
        setError(localizeApiDetail(typeof d === 'string' ? d : d?.melding) || t('finansiering.loadFailed'))
      })
      .finally(() => setChecking(false))
  }

  if (loading) {
    return <div className="animate-pulse h-48 bg-gray-100 dark:bg-dark-border rounded-sm" />
  }
  if (!data) {
    return <div className="card p-6 text-red-600 dark:text-red-400">{error}</div>
  }

  const datoTekst = (iso: string) => formatNorwegianDate(new Date(iso))
  const kvoteDekker = data.antall_biler === null || data.kvote.gjenstaende >= data.antall_biler

  return (
    <div className="space-y-4" data-testid="finansiering-tab">
      <div className="card p-4 flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-gray-600 dark:text-gray-300">
          <p>{t('finansiering.intro')}</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="finansiering-sjekket">
            {data.checked_at ? t('finansiering.sjekket', { dato: datoTekst(data.checked_at) }) : t('finansiering.aldriSjekket')}
            {data.neste_sjekk && !data.kan_sjekkes && !data.over_grense && (
              <> &middot; {t('finansiering.nesteSjekk', { dato: datoTekst(data.neste_sjekk) })}</>
            )}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-testid="finansiering-kvote">
            {t('finansiering.kvote', { brukt: data.kvote.brukt, budsjett: data.kvote.budsjett })}
            {data.kan_sjekkes && !kvoteDekker && (
              <> &middot; {t('finansiering.kvoteForLiten', { count: data.antall_biler ?? 0 })}</>
            )}
          </p>
        </div>
        <button
          type="button"
          onClick={hent}
          disabled={!data.kan_sjekkes || !kvoteDekker || checking}
          className="px-4 py-2 rounded-sm bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="finansiering-hent"
        >
          {checking ? t('finansiering.henter', { count: data.antall_biler ?? 0 }) : t('finansiering.hent')}
        </button>
      </div>

      {error && <div className="card p-4 text-sm text-red-600 dark:text-red-400" role="alert">{error}</div>}

      {data.over_grense && (
        <div className="card p-4 text-sm text-gray-600 dark:text-gray-300" data-testid="finansiering-over-grense">
          <Info className="w-4 h-4 inline mr-2" />{t('finansiering.overGrense', { max: data.maks_biler })}
        </div>
      )}
      {data.antall_biler === 0 && (
        <div className="card p-4 text-sm text-gray-600 dark:text-gray-300">{t('finansiering.ingenBiler')}</div>
      )}

      {data.biler.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2 text-xs">
            {(['heftelse', 'leasing_svv', 'ingen', 'ikke_sjekket'] as const).map(s => (
              <span key={s} className={`px-2 py-1 rounded-sm font-medium ${STATUS_STYLES[s]}`}>
                {t(`finansiering.status.${s}`)}: {data.antall[s]}
              </span>
            ))}
          </div>

          {data.per_rettighetshaver.length > 0 && (
            <div className="card p-4">
              <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                <Landmark className="w-4 h-4 inline mr-2" />{t('finansiering.perRettighetshaver')}
              </h3>
              <table className="data-table data-table-compact w-full" data-testid="finansiering-per-rettighetshaver">
                <thead>
                  <tr>
                    <th className="text-left">{t('finansiering.rettighetshaver')}</th>
                    <th className="text-left">{t('finansiering.type')}</th>
                    <th className="text-right">{t('finansiering.biler')}</th>
                    <th className="text-right">{t('finansiering.sumBelop')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.per_rettighetshaver.map(g => (
                    <tr key={`${g.org_nr ?? g.navn}-${g.kategori}`}>
                      <td>{g.navn ?? '—'}</td>
                      <td>{g.kategori ? t(`finansiering.kategori.${g.kategori}`) : '—'}</td>
                      <td className="text-right">{g.antall_biler}</td>
                      <td className="text-right whitespace-nowrap">{formatBelop(g.sum_belop)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="card p-4 overflow-x-auto">
            <table className="data-table data-table-compact w-full" data-testid="finansiering-biler">
              <thead>
                <tr>
                  <th className="text-left">{t('finansiering.kjennemerke')}</th>
                  <th className="text-left">{t('finansiering.merkeModell')}</th>
                  <th className="text-left">{t('finansiering.type')}</th>
                  <th className="text-left">{t('finansiering.rettighetshaver')}</th>
                  <th className="text-right">{t('finansiering.belop')}</th>
                  <th className="text-left">{t('finansiering.tinglyst')}</th>
                </tr>
              </thead>
              <tbody>
                {data.biler.flatMap(bil => {
                  const merkeModell = [bil.merke, bil.modell].filter(Boolean).join(' ') || '—'
                  if (bil.status === 'leasing_svv' && bil.svv_leasing) {
                    return [(
                      <tr key={bil.kjennemerke}>
                        <td className="font-mono whitespace-nowrap">{bil.kjennemerke}</td>
                        <td>{merkeModell}</td>
                        <td>
                          <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${STATUS_STYLES.leasing_svv}`}>
                            {t('finansiering.status.leasing_svv')}
                          </span>
                        </td>
                        <td>
                          {bil.svv_leasing.navn ?? '—'}
                          <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({t('finansiering.fraSvv')})</span>
                        </td>
                        <td className="text-right">—</td>
                        <td>—</td>
                      </tr>
                    )]
                  }
                  if (bil.heftelser.length === 0) {
                    return [(
                      <tr key={bil.kjennemerke}>
                        <td className="font-mono whitespace-nowrap">{bil.kjennemerke}</td>
                        <td>{merkeModell}</td>
                        <td colSpan={4}>
                          <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${STATUS_STYLES[bil.status]}`}>
                            {t(`finansiering.status.${bil.status}`)}
                          </span>
                        </td>
                      </tr>
                    )]
                  }
                  return bil.heftelser.map((h, i) => (
                    <tr key={`${bil.kjennemerke}-${h.dokumentnummer ?? i}`}>
                      <td className="font-mono whitespace-nowrap">{i === 0 ? bil.kjennemerke : ''}</td>
                      <td>{i === 0 ? merkeModell : ''}</td>
                      <td>{h.type ?? '—'}</td>
                      <td>
                        {h.rettighetshaver_navn ?? '—'}
                        {h.rettighetshaver_etikett && (
                          <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">({h.rettighetshaver_etikett})</span>
                        )}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        {formatBelop(h.belop)}
                        {(h.antall_kjoretoy ?? 1) > 1 && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {t('finansiering.felles', { count: h.antall_kjoretoy ?? 0 })}
                          </div>
                        )}
                      </td>
                      <td className="whitespace-nowrap">{h.tinglyst ? formatNorwegianDate(h.tinglyst) : '—'}</td>
                    </tr>
                  ))
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
